<template>
  <!-- User message -->
  <div v-if="isCurrentUser" class="message-container user-message">
    <div class="message-content user-content">
      <!-- Sender info and channel -->
      <div class="message-header user-header">
        <img src="@/assets/MyPatient/avatar.svg" alt="Avatar" class="avatar-img"/>
        <span class="sender-name">{{ senderName }}</span>
        <MessageSourceChip :message-source="messageSource" />
      </div>
      
      <!-- Message bubble -->
      <div class="message-bubble">
        <!-- Text and/or Image messages (combined) -->
        <div class="w-full flex justify-end">
          <Message
            v-if="messageData.msg && messageData.msg.trim()"
            :messageData="messageData"
            :rc_user_id="rc_user_id"
          />
        </div>
        <div v-if="isImageMessage" class="w-full justify-end items-end flex flex-col gap-2" :class="{ 'mt-2': messageData.msg && messageData.msg.trim() }">
          <MessageCardImage
            v-for="(attachment, index) in imageAttachments"
            :key="`user-image-${index}`"
            :attachment="attachment"
          />
        </div>
        
        <!-- Document message card -->
        <MessageCardDoc
          v-else-if="isDocumentMessage"
          :messageData="messageData"
          :rc_user_id="rc_user_id"
          :patientInfo="patientInfo"
        />
        
        <!-- Order status message card -->
        <MessageCardOrderStatus
          v-else-if="isOrderStatusMessage"
          :messageData="messageData"
          :rc_user_id="rc_user_id"
        />
        
        <!-- Appointment message card -->
        <MessageCardAppointment
          v-else-if="isAppointmentMessage"
          :messageData="messageData"
          :rc_user_id="rc_user_id"
          @edit-appointment="$emit('edit-appointment', $event)"
        />
        
        <!-- Task message card -->
        <MessageCardTask
          v-else-if="isTaskMessage"
          :messageData="messageData"
          :rc_user_id="rc_user_id"
          @edit-task="$emit('edit-task', $event)"
        />
        
        <div class="message-timestamp user-timestamp w-full text-start">
          {{ formattedTimestamp }}
        </div>
      </div>
    </div>
  </div>
  
  <!-- Bot message -->
  <div v-else-if="isBotMessage" class="message-container bot-centered-message">
    <div class="message-content bot-user-content">
      <!-- Sender info and channel -->
      <div class="message-header bot-user-header">
        System Message: {{ formattedTimestamp }}
      </div>

      <!-- Message bubble -->
      <div class="message-bubble">
        <!-- Text and/or Image messages (combined) -->
        <Message
          v-if="messageData.msg && messageData.msg.trim()"
          :messageData="messageData"
          :rc_user_id="rc_user_id"
        />
        <div v-if="isImageMessage" class="flex flex-col gap-2" :class="{ 'mt-2': messageData.msg && messageData.msg.trim() }">
          <MessageCardImage
            v-for="(attachment, index) in imageAttachments"
            :key="`user-image-${index}`"
            :attachment="attachment"
          />
        </div>
        
        <!-- Document message card -->
        <MessageCardDoc
          v-else-if="isDocumentMessage"
          :messageData="messageData"
          :rc_user_id="rc_user_id"
          :patientInfo="patientInfo"
        />
        
        <!-- Order status message card -->
        <MessageCardOrderStatus
          v-else-if="isOrderStatusMessage"
          :messageData="messageData"
          :rc_user_id="rc_user_id"
        />
        
        <!-- Appointment message card -->
        <MessageCardAppointment
          v-else-if="isAppointmentMessage"
          :messageData="messageData"
          :rc_user_id="rc_user_id"
          @edit-appointment="$emit('edit-appointment', $event)"
        />
        
        <!-- Task message card -->
        <MessageCardTask
          v-else-if="isTaskMessage"
          :messageData="messageData"
          :rc_user_id="rc_user_id"
          @edit-task="$emit('edit-task', $event)"
        />
      </div>
    </div>
  </div>
  
  <!-- Not user message -->
  <div v-else class="message-container not-user-message">
    <div class="message-content not-user-content">
      <!-- Sender info and channel -->
      <div class="message-header not-user-header">
        <span class="sender-name">{{ senderName }}</span>
        <MessageSourceChip :message-source="messageSource" />
      </div>
      
      <!-- Message bubble -->
      <div class="message-bubble">
        <!-- Text and/or Image messages (combined) -->
        <div class="w-full flex justify-start">
          <Message
            v-if="messageData.msg && messageData.msg.trim()"
            :messageData="messageData"
            :rc_user_id="rc_user_id"
          />
        </div>
        <div v-if="isImageMessage" class="flex flex-col gap-2" :class="{ 'mt-2': messageData.msg && messageData.msg.trim() }">
          <MessageCardImage
            v-for="(attachment, index) in imageAttachments"
            :key="`user-image-${index}`"
            :attachment="attachment"
          />
        </div>
        
        <!-- Document message card -->
        <MessageCardDoc
          v-else-if="isDocumentMessage"
          :messageData="messageData"
          :rc_user_id="rc_user_id"
          :patientInfo="patientInfo"
        />
        
        <!-- Order status message card -->
        <MessageCardOrderStatus
          v-else-if="isOrderStatusMessage"
          :messageData="messageData"
          :rc_user_id="rc_user_id"
        />
        
        <!-- Appointment message card -->
        <MessageCardAppointment
          v-else-if="isAppointmentMessage"
          :messageData="messageData"
          :rc_user_id="rc_user_id"
        />
        
        <!-- Task message card -->
        <MessageCardTask
          v-else-if="isTaskMessage"
          :messageData="messageData"
          :rc_user_id="rc_user_id"
        />
        
        <div class="message-timestamp not-user-timestamp w-full text-start">
          {{ formattedTimestamp }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Message from './message.vue';
import MessageCardDoc from './messageCardDoc.vue';
import MessageCardOrderStatus from './messageCardOrderStatus.vue';
import MessageCardAppointment from './messageCardAppointment.vue';
import MessageCardTask from './messageCardTask.vue';
import MessageCardImage from './messageCardImage.vue';
import MessageSourceChip from './MessageSourceChip.vue';

export default {
  name: 'MessageUnit',
  components: {
    Message,
    MessageCardDoc,
    MessageCardOrderStatus,
    MessageCardAppointment,
    MessageCardTask,
    MessageCardImage,
    MessageSourceChip
  },
  props: {
    messageData: {
      type: Object,
      required: true
    },
    rc_user_id: {
      type: String,
      required: true
    },
    patientInfo: {
      type: Object,
      required: true
    },
    messageType: {
      type: String,
      default: ''
    }
  },
  computed: {
    isCurrentUser() {
      return this.messageData.u._id === this.rc_user_id;
    },
    
    isBotMessage() {
      return this.messageData.u._id === 'oafKicny6TymdzNxQ' || 
             this.messageData.u._id === 'EuMKAsjDZTsLc74Kw';
    },
    
    
    senderName() {
      // Current user
      if (this.isCurrentUser) {
        return this.$store.getters['login/userInfo']?.customer_name || 'You';
      }
      
      // Bot message - show as system message
      if (this.isBotMessage) {
        return `System Message: ${this.formattedTimestamp}`;
      }
      
      // Other user - parse customer_id from username
      const username = this.messageData.u?.username;
      if (username && username.startsWith('doctor_')) {
        const parts = username.split('_');
        if (parts.length >= 3) {
          const customerId = parseInt(parts[1]);
          const customer = this.$store.getters['myPatients/practiceInfo/getCustomerById'](customerId);
          if (customer) {
            const firstName = customer.customer_first_name || '';
            const lastName = customer.customer_last_name || '';
            const suffix = customer.customer_suffix || '';
            return suffix ? `${firstName} ${lastName}, ${suffix}`.trim() : `${firstName} ${lastName}`.trim();
          }
        }
      }
      
      // Fallback to patient name
      return `${this.patientInfo.patientBasicInfo.firstName} ${this.patientInfo.patientBasicInfo.lastName}`;
    },
    
    formattedTimestamp() {
      const date = this.messageData.ts.$date ? this.messageData.ts.$date : this.messageData.ts;
      const messageDate = new Date(date);
      
      // Format as MM-DD-YYYY HH:MM AM/PM
      const dateStr = messageDate.toLocaleDateString('en-US', { 
        month: '2-digit', 
        day: '2-digit', 
        year: 'numeric' 
      }).replace(/\//g, '-');
      
      const timeStr = messageDate.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      });
      
      return `${dateStr}   ${timeStr}`;
    },
    
    isTextMessage() {
      // Use parent's message type detection
      return this.messageType === 'au' || !this.messageType || this.messageType === '';
    },
    
    isDocumentMessage() {
      // Use parent's message type detection
      return this.messageType === 'doc_card';
    },
    
    isOrderStatusMessage() {
      // Use parent's message type detection
      return this.messageType === 'order_card';
    },
    
    isAppointmentMessage() {
      // Use parent's message type detection
      return this.messageType === 'appointment_card';
    },
    
    isTaskMessage() {
      // Use parent's message type detection
      return this.messageType === 'task_card';
    },
    
    isImageMessage() {
      // Use parent's message type detection
      return this.messageType === 'image';
    },
    
    messageSource() {
      // Extract message_source from attachments
      const source = this.messageData?.attachments?.[0]?.fields?.find(field => field.title === 'message_source')?.value;
      // Return the source if it exists, otherwise default to 'in_app'
      return source || 'in_app';
    },
    
    imageAttachments() {
      // Filter attachments to get only image attachments
      return (this.messageData?.attachments || []).filter(att => att.title === 'image_uploaded');
    }
  }
}
</script>

<style scoped>
.message-container {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  max-width: 100%;
}

/* User messages (right side) */
.user-message {
  flex-direction: row-reverse;
}

/* Not user messages (left side) */
.not-user-message {
  flex-direction: row;
}

/* Bot centered messages */
.bot-centered-message {
  justify-content: center;
}

.avatar-img {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.message-content {
  flex: 1;
  min-width: 0;
  max-width: 100%;
}

.user-content {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-left: 118px;
}

.not-user-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-right: 118px;
}

.bot-user-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 80%;
}

.message-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.user-header {
  flex-direction: row-reverse;
}

.not-user-header {
  flex-direction: row;
}

.bot-user-header {
  justify-content: center;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #6B7280;
}

.sender-name {
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #333333;
}

.channel-tag {
  background-color: #e6f3ff;
  color: #004879;
  padding: 2px 8px;
  border-radius: 12px;
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
}

.message-bubble {
  position: relative;
  max-width: 100%;
  word-wrap: break-word;
}

.message-timestamp {
  margin-top: 4px;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 11px;
  line-height: 16px;
  color: #999999;
  letter-spacing: 0.5px;
}

.user-timestamp {
  text-align: right;
}

.not-user-timestamp {
  text-align: left;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .message-content {
    max-width: calc(100vw - 80px);
  }
}
</style>
